// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, intlShape } from 'react-intl';
import update from 'immutability-helper';
import { animateScroll as scroll } from 'react-scroll';
import Loading from '../../../../elements/Loading/Loading';
import Modal from '../../../../elements/Modal/Modal';
import Product, { ProductLoading } from '../../../../elements/Product/Product';
import LookingForTravelerContainer from '../looking-for-traveler/containers/LookingForTravelerContainer';
import RegisterPurchaseContainer from '../register-purchase/containers/RegisterPurchaseContainer';
import CourierFlowContainer from '../courier-flow/containers/CourierFlowContainer';
import OrderCanceled from '../order-canceled/components/OrderCanceled';
import { groupItems } from '../../../new-order/QuoteStep/utils';
import classNames from 'classnames/bind';
import CourierInfoMini from './CourierInfoMini/CourierInfoMini';
import PurchaseAddress, { PurchaseAddressLoading } from './PurchaseAddress/PurchaseAddress';
import PurchaseSummary, { PurchaseSummaryLoading } from './PurchaseSummary/PurchaseSummary';
import UnconfirmedPaymentContainer from '../unconfirmed-payment/container/UnconfirmedPaymentContainer';
import TravelerInfo from './TravelerInfo/TravelerInfo';
import PurchasedContainer from '../purchased/containers/PurchasedContainer';
import InTransitContainer from '../in-transit/containers/InTransitContainer';
import FeedbackContainer from '../feedback/containers/FeedbackContainer';
import StatusBoard from './StatusBoard/StatusBoard';
import SingleProduct, {
  SingleProductButtonContent,
  SingleProductData,
} from '../../../../elements/SingleProduct/SingleProduct';
import commonStyle from '../../components/OrderCommon.scss';

import icons from '../../../../assets/styles/icons.scss';
import productStyle from '../../../../elements/Product/Product.scss';
import style from './Purchase.scss';

import type { Node } from 'react';
import type { BagItem, Order, OrderItem, ShoppingCartItem } from '../../../../entities';
import type { HTTPError } from '../../../../error';
import type { Match } from '../../../../router';
import type { OrderType } from '../../duck';
import type { LocalValuesState } from '../../../local-values/duck';
import type { ItemsByStore } from '../../../new-order/QuoteStep/components/QuoteSummary/QuoteSummary';
import type { ProductType } from '../../../../elements/SingleProduct/SingleProduct';
import type { Props as ProductProps } from '../../../../elements/Product/Product';
import type { LocaleState } from '../../../locale/duck';

type Props = {
  +match: Match;
  +localeState: LocaleState;
  +localValues: LocalValuesState;
  +fetchOrder: (orderId: string, orderType: OrderType) => void;
  +isFetchingOrder?: boolean;
  +order?: Order;
  +resetState: () => void;
  +resetAlertState: () => void;
  +isProgressingOrder?: boolean;
  +progressError?: HTTPError;
};

type State = {
  itemsOpen: { [key: string]: boolean };
};

type Item = BagItem | ShoppingCartItem;

export default class Purchase extends Component<Props, State> {
  orderId: string;

  constructor(props: Props) {
    super(props);
    // NOTE: By default, the first one is opened
    this.state = {
      itemsOpen: {
        '0': true,
      },
    };
  }

  componentDidMount() {
    const { match } = this.props;
    /*if (!order && match.params) {
      fetchOrder(match.params.id, 'purchase');
    }*/
    this.orderId = match.params.id;
  }

  componentDidUpdate(prevProps: Props) {
    const { fetchOrder, match, progressError } = this.props;

    if (!prevProps.progressError && !!progressError) {
      this.scrollAnimateTop();
    }

    if (prevProps.match.params.id !== match.params.id) {
      fetchOrder(match.params.id, 'purchase');
    }
  }

  componentWillUnmount() {
    const { resetAlertState, resetState } = this.props;
    resetAlertState();
    resetState();
  }

  scrollAnimateTop = () =>
    scroll.scrollTo(0, {
      duration: 400,
    });

  handleOrderOpen = (index: number) => {
    this.setState((state: State) => {
      return {
        itemsOpen: update(state.itemsOpen, {
          [`${index}`]: {
            $set: !state.itemsOpen[`${index}`],
          },
        }),
      }
    });
  };

  buildTravelerInfo = (): Node => {
    const { order } = this.props;
    return (
      <Fragment>
        {order && (order: Order).shipments && (order: Order).shipments.length > 0 &&
        <section className={style.purchaseSection}>
          <CourierInfoMini courier={order.shipments[0].courier}/>
        </section>}
        {order && (order: Order).serviceTier && (order: Order).shipments && (order: Order).shipments.length === 0 &&
        <TravelerInfo order={order}/>}
      </Fragment>
    )
  };

  buildSummary = (contentBottom?: Node): Node => {
    const { order, localValues } = this.props;
    const address = (order: Order).shippingAddress;
    
    return (
      <Fragment>
        <section className={style.purchaseSection}>
          <PurchaseSummary localValues={localValues}
                           order={order}
                           contentBottom={contentBottom}/>
        </section>
        <section className={style.purchaseSection}>
          <PurchaseAddress address={address}
                           localValues={localValues}
                           deliveryCountry={(order: Order).deliveryCountry.code}/>
        </section>
      </Fragment>
    );
  };

  buildProducts = (renderContent?: (props: ProductProps) => Node): Node => {
    const { itemsOpen } = this.state;
    const { localValues, order, localeState } = this.props;
    let idx = -1;
    const itemsByStores: ?ItemsByStore[] = order && groupItems(order.items);
    return (
      <Fragment>
        <FormattedMessage id="order.summary.title">{
          txt => <h2 className={commonStyle.title}>{txt}</h2>
        }</FormattedMessage>
        {itemsByStores && itemsByStores.map((store: ItemsByStore, n: number) =>
          <section key={n}
                   className={commonStyle.storesWrap}>
            <section className={commonStyle.storeInfoWrap}>
              <FormattedMessage id="newOrder.quote.products.soldBy">{
                txt => <span className={commonStyle.storeInfoLabel}>{txt}:</span>
              }</FormattedMessage>
              {store.logo ? <img className={commonStyle.logo}
                                 src={store.logo}
                                 alt={store.name}/> : <span>{store.name}</span>}
              {!store.items.some((item: Item) => item.shipping !== '0.00') &&
              <FormattedMessage id="newOrder.quote.products.free">{
                txt => <span className={commonStyle.free}>
                  <i className={icons.symbolCheck}/>{txt}</span>
              }</FormattedMessage>}
            </section>
            {store.items.map((item: Item) => {
              let productType: ProductType = 'SUCCESS';
              let renderInvoice: (props: ProductProps) => Node;
              let subtitleIntl: string = 'product.state.success.received';
              if ((item: OrderItem).state === 'CANCELED' || (order &&
                ['BUYER_CANCELED',
                  'TRAVELER_CANCELED',
                  'QEMPO_CANCELED',
                  'QEMPO_CANCELED_FOR_FRAUD'].indexOf(order.state) !== -1)) {
                productType = 'CANCELED';
                subtitleIntl = 'product.state.canceled';
              } else if (order &&
                ['CREATED', 'AVAILABLE', 'RESERVED'].indexOf(order.state) !== -1) {
                productType = 'VIEW';
                subtitleIntl = 'product.state.view.pending';
              } else {
                if ((item: OrderItem).state === 'PENDING') {
                  subtitleIntl = 'product.state.success.purchased';
                }
                renderInvoice = (props: ProductProps): Node => {
                  const { externalInvoice, tracking } = (props.item: OrderItem);
                  return (
                    <section className={productStyle.bottomWrapper}>
                      {tracking && tracking.trackingNumber && (
                        <section className={productStyle.tracking}>
                          <FormattedMessage id="product.tracking.label">{
                            text => <span>{text}</span>
                          }</FormattedMessage>
                          <p>{tracking.trackingNumber}</p>
                        </section>)}
                      <FormattedMessage id="product.invoice">{
                        txt => <a className={productStyle.invoice}
                                  href={`${externalInvoice ? externalInvoice.image : ''}`}
                                  target="_blank"><i className={icons.iconInvoiceLight}/>{txt}</a>
                      }</FormattedMessage>
                    </section>)
                };
              }
              idx++;
              return <Product key={idx}
                              index={idx}
                              item={item}
                              open={!!itemsOpen[`${idx}`]}
                              isLoading={false}
                              handleOpen={this.handleOrderOpen}
                              localeState={localeState}
                              categoriesMap={localValues.categoriesMap}
                              type={productType}
                              onlineStore={item.onlineStore}
                              renderContent={renderContent || renderInvoice}
                              subtitle={subtitleIntl}
                              userType="purchaser"/>;
            })}
          </section>)}
      </Fragment>
    );
  };

  buildSingleProducts = (renderContent?: (item: Item) => Node): Node => {
    const { order, localeState, localValues } = this.props;
    const itemsByStores: ItemsByStore[] = groupItems(!!order ? order.items : []);
    return (
      itemsByStores.map((store: ItemsByStore, n: number) =>
        <div key={n}
             className={style.singleProductsWrap}>
          <div className={commonStyle.storeInfoWrap}>
            <FormattedMessage id="newOrder.quote.products.soldBy">{
              txt => <p className={commonStyle.soldBy}>{txt}: {store.name}</p>
            }</FormattedMessage>
          </div>
          {store.items.map((item: Item) => {
            let productType: ProductType = 'SUCCESS';
            let subtitleIntl: string = 'product.state.success.received';
            if ((item: OrderItem).state === 'CANCELED' || (order &&
              ['BUYER_CANCELED',
                'TRAVELER_CANCELED',
                'QEMPO_CANCELED',
                'QEMPO_CANCELED_FOR_FRAUD'].indexOf(order.state) !== -1)) {
              productType = 'CANCELED';
              subtitleIntl = 'product.state.canceled';
            } else if (order &&
              ['CREATED', 'CREATED_FOR_COURIER', 'AVAILABLE',
                'AVAILABLE_FOR_COURIER', 'RESERVED'].indexOf(order.state) !== -1) {
              productType = 'VIEW';
              subtitleIntl = 'product.state.view.pending';
            } else {
              if ((item: OrderItem).state === 'PENDING') {
                subtitleIntl = 'product.state.success.purchased';
              }
            }
            return (
              <SingleProduct key={item.index}
                             localeState={localeState}
                             index={item.index}
                             item={item}
                             userType="purchaser"
                             subtitleIntl={subtitleIntl}
                             renderContent={renderContent}
                             buttonChildren={<SingleProductButtonContent type={productType}
                                                                         amount={item.amount}
                                                                         name={item.name}
                                                                         image={item.image}
                                                                         imageURI={item.imageURI}/>}>
                <SingleProductData
                  categoryId={{
                    intl: 'singleProduct.categoryId.label',
                    content: localValues.categoriesMap ?
                      (localValues.categoriesMap: any)[`${item.itemCategoryId}`]
                        .translations[localeState.locale.toUpperCase()].name : '',
                  }}
                  detail={{ content: item.detail }}
                  originalPackaging={{
                    intl: "singleProduct.packaging.label",
                    content: item.originalPackaging ?
                      this.context.intl.formatMessage({ id: "singleProduct.packaging.withBox" }) :
                      this.context.intl.formatMessage({ id: "singleProduct.packaging.withoutBox" }),
                  }}
                  price={{ intl: "singleProduct.price.label", content: item.price }}
                  total={{
                    intl: "singleProduct.total.label",
                    content: (parseFloat(item.price) * item.amount).toFixed(2),
                  }}
                  url={{ intl: "singleProduct.url.label", content: item.url }}
                  weight={{ intl: "singleProduct.weight.label", content: item.weight }}>
                </SingleProductData>
              </SingleProduct>);
          })}
        </div>)
    );
  };

  buildStatusBoard = (): Node => {
    const { localeState, order } = this.props;
    if (typeof order === 'undefined') return;
    if (!!order.items) {
      return (
        <StatusBoard localeState={localeState}
                     lastMileShippings={order.lastMileShippings}
                     items={order.items}/>)
    }
  };

  getPurchaseComponent(): ?Node {
    const { isFetchingOrder, order } = this.props;
    if (!order || isFetchingOrder) {
      return;
    }
    // We constantly wanna bind whenever renders happens
    switch (order.state) {
      case 'CREATED':
      case 'CREATED_FOR_COURIER':
        return <UnconfirmedPaymentContainer buildProducts={this.buildSingleProducts}
                                            buildSummary={this.buildSummary}/>;
      case 'AVAILABLE':
      case 'RESERVED':
        return <LookingForTravelerContainer buildProducts={this.buildSingleProducts}
                                            buildSummary={this.buildSummary}/>;
      case 'AVAILABLE_FOR_COURIER':
        return <RegisterPurchaseContainer buildProducts={this.buildSingleProducts}
                                          buildSummary={this.buildSummary}/>;
      case 'IN_COURIER_FLOW':
        return <CourierFlowContainer buildStatusBoard={this.buildStatusBoard}
                                     buildProducts={this.buildSingleProducts}
                                     buildSummary={this.buildSummary}/>;
      case'PURCHASED':
      case'RECEIVED':
      case'ARRIVED':
      case'IN_QEMPO':
      case'IN_QEMPO_CONFIRMED':
        return <PurchasedContainer buildStatusBoard={this.buildStatusBoard}
                                   buildProducts={this.buildProducts.bind(this)}
                                   buildSummary={this.buildSummary}
                                   buildTravelerInfo={this.buildTravelerInfo}/>;
      case 'BUYER_CANCELED':
      case 'TRAVELER_CANCELED':
      case 'QEMPO_CANCELED':
      case 'QEMPO_CANCELED_FOR_FRAUD':
        return <OrderCanceled buildProducts={this.buildProducts.bind(this)}
                              buildSummary={this.buildSummary}/>;
      case 'SHIPPING':
      case 'DELIVERED':
        return <InTransitContainer order={order}
                                   buildStatusBoard={this.buildStatusBoard}
                                   buildProducts={this.buildProducts.bind(this)}
                                   buildSummary={this.buildSummary}
                                   buildTravelerInfo={this.buildTravelerInfo}/>;
      case 'DELIVERY_CONFIRMED':
        return <FeedbackContainer order={order}
                                  buildStatusBoard={this.buildStatusBoard}
                                  buildProducts={this.buildProducts.bind(this)}
                                  buildSummary={this.buildSummary}
                                  buildTravelerInfo={this.buildTravelerInfo}/>;
      default:
        return <p>default</p>;
    }
  }

  render() {
    const { isFetchingOrder, order, isProgressingOrder } = this.props;
    const purchaseComponent = this.getPurchaseComponent();
    const loadingComponent =
      <main className={commonStyle.mainWrap}>
        <section className={classNames(commonStyle.wrap, commonStyle.leftWrap)}>
          <FormattedMessage id="order.summary.title">{
            txt => <h2 className={commonStyle.title}>{txt}</h2>
          }</FormattedMessage>
          {[0, 1, 2].map((_, n: number) => <ProductLoading key={n}/>)}
        </section>
        <section className={classNames(commonStyle.wrap, commonStyle.rightWrap)}>
          <section className={style.purchaseSection}>
            <PurchaseSummaryLoading orderId={!!order ? order.id : this.orderId}/>
          </section>
          <section className={style.purchaseSection}>
            <PurchaseAddressLoading/>
          </section>
        </section>
      </main>;
    // return (!order || isFetchingOrder ? loadingComponent : purchaseComponent);
    return (<Fragment>
      {!order || isFetchingOrder ? loadingComponent : purchaseComponent}
      <Modal handleClose={() => undefined}
             open={isProgressingOrder}
             transparent
             maskColor="white"
             showCloseButton={false}>
        <Loading type="circle"
                 color="lightBlue"
                 className={style.loading}/>
      </Modal>
    </Fragment>);
  }
}

Purchase.propTypes = {
  match: PropTypes.object.isRequired,
  localeState: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  fetchOrder: PropTypes.func.isRequired,
  isFetchingOrder: PropTypes.bool,
  order: PropTypes.object,
  resetState: PropTypes.func.isRequired,
  resetAlertState: PropTypes.func.isRequired,
  isProgressingOrder: PropTypes.bool,
  progressError: PropTypes.object,
};

Purchase.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/order/Purchase/components/Purchase.jsx